import { Injectable, computed, inject, signal } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

export type AccessDeniedVariant = 'denied' | 'notFound';

export interface AccessDeniedState {
  variant: AccessDeniedVariant;
  /** The backend's own reason, or null to use the shared component's default copy. */
  message: string | null;
  /** Optional "way out" navigation for the panel; the overlay defaults it to the dashboard. */
  homeLink: unknown[] | string | null;
  homeLabel: string | null;
}

export interface AccessDeniedOptions {
  message?: string | null;
  homeLink?: unknown[] | string | null;
  homeLabel?: string | null;
}

/**
 * Backs the single global access-denied surface (`app-access-denied-overlay`, mounted once at the app
 * root). It is the ONE place that renders the shared `app-access-denied` panel — feature components never
 * embed it; they call {@link showDenied} / {@link showNotFound} and the overlay draws it over the page.
 *
 * Two entry points reach here:
 *  - `forbiddenInterceptor`, for an **opted-in** read (a request carrying `FORBIDDEN_OVERLAY` — a screen's
 *    one authoritative record fetch) that comes back 403;
 *  - a screen that has already resolved a terminal "can't show this" state itself (a document's 404 / null
 *    body, or a read page whose scoped probes are all exhausted — cases the interceptor can't detect,
 *    because those 403s are benign fall-throughs).
 *
 * The overlay auto-dismisses on the next navigation, so it never lingers onto the page the user moves to.
 */
@Injectable({ providedIn: 'root' })
export class AccessDeniedService {
  private readonly _state = signal<AccessDeniedState | null>(null);
  readonly state = this._state.asReadonly();
  readonly active = computed(() => this._state() !== null);

  constructor() {
    inject(Router).events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe(() => this._state.set(null));
  }

  /** Access denied (403 / out of scope) — `message` is the reason the backend gave. */
  showDenied(message: string | null, opts?: AccessDeniedOptions): void {
    this.set('denied', message, opts);
  }

  /** Record not found / no longer available. */
  showNotFound(opts?: AccessDeniedOptions): void {
    this.set('notFound', opts?.message ?? null, opts);
  }

  /** Hide the overlay (also happens automatically on the next navigation). */
  dismiss(): void {
    this._state.set(null);
  }

  private set(variant: AccessDeniedVariant, message: string | null, opts?: AccessDeniedOptions): void {
    this._state.set({
      variant,
      message,
      homeLink: opts?.homeLink ?? null,
      homeLabel: opts?.homeLabel ?? null,
    });
  }
}
